import { CaretDown, CaretUp, Backspace, CheckCircle, Lightbulb } from '@phosphor-icons/react';
import * as React from 'react';

import { EditCount } from '../review-list/edit-count';

import { Button } from '@/components/ui/button';
import { brandShadowEffect } from '@/lib/styles';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ButtonGroup } from '@/components/ui/button-group';
import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/convos/status-badge';

export interface SuggestionsHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  isNew: boolean;
  suggestionsCount: number;
  sourceIcon: React.ReactNode;
  sourceDescription: string | null;
  currentArticleIndex: number;
  totalArticles: number;
  displayTotalArticles?: number;
  showActionButtons?: boolean;
  onNextArticle: () => void;
  onPreviousArticle: () => void;
  onRejectAllSuggestions: () => void;
  onAcceptAllSuggestions: () => void;
  onAcceptAndPublish?: () => void;
  breadcrumb?: string;
  onOpenArticleLink?: () => void;
  onSourceLinkClick?: () => void;
  suggestionReasonBody?: string;
}

export const REASON_TITLE = 'Why is Brainfish suggesting this?';

export const SuggestionsHeader = React.forwardRef<HTMLDivElement, SuggestionsHeaderProps>(function SuggestionsHeader(
  {
    title,
    isNew,
    suggestionsCount,
    sourceIcon,
    sourceDescription,
    currentArticleIndex,
    totalArticles,
    displayTotalArticles,
    showActionButtons = true,
    onNextArticle,
    onPreviousArticle,
    onRejectAllSuggestions,
    onAcceptAllSuggestions,
    onAcceptAndPublish,
    breadcrumb,
    onOpenArticleLink,
    onSourceLinkClick,
    suggestionReasonBody,
    className,
    ...props
  },
  ref,
) {
  const splitButtonRef = React.useRef<HTMLDivElement>(null);
  const [whyOpen, setWhyOpen] = React.useState(false);
  const hasNextArticle = totalArticles > 0 && currentArticleIndex < totalArticles - 1;
  const hasPreviousArticle = currentArticleIndex > 0;
  const currentArticle = totalArticles === 0 ? 0 : currentArticleIndex + 1;
  const totalArticlesForDisplay = displayTotalArticles ?? totalArticles;
  const hasDisplayArticles = totalArticlesForDisplay > 0;

  let status: React.ReactNode | undefined;
  if (isNew) {
    status = <StatusBadge variant="success">New</StatusBadge>;
  } else if (suggestionsCount) {
    status = <EditCount count={suggestionsCount} />;
  }

  return (
    <div ref={ref} className={cn('flex flex-col gap-4', className)} {...props}>
      {/* Header + Navigation */}
      <div className="flex justify-between items-center">
        <h1 className="heading-lg">
          {title}
          {/* Breadcrumbs */}
          {onOpenArticleLink && breadcrumb && (
            <Button
              variant="link"
              size="sm"
              onClick={onOpenArticleLink}
              aria-label="Open article"
              className="block px-0 text-subtle text-xs"
            >
              {breadcrumb}
            </Button>
          )}
        </h1>
        {/* up/down carets */}
        <div className="flex items-center">
          <p className="text-xs text-foreground">
            <span aria-hidden="true">{hasDisplayArticles ? `${currentArticle}/${totalArticlesForDisplay}` : '0'}</span>
            <span className="sr-only" aria-live="polite">
              {hasDisplayArticles ? `Article ${currentArticle} of ${totalArticlesForDisplay}` : 'No articles'}
            </span>
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={!hasNextArticle}
                onClick={onNextArticle}
                aria-label="Go to next article"
              >
                <CaretDown aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" bgColor="--bfc-foreground" className="text-background">
              Next Article
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={!hasPreviousArticle}
                onClick={onPreviousArticle}
                aria-label="Go to previous article"
              >
                <CaretUp aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" bgColor="--bfc-foreground" className="text-background">
              Previous Article
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Status, Source + Actions */}
      <div className="flex-1 px-2 pb-2 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {status}
          {sourceDescription &&
            (onSourceLinkClick ? (
              <Button
                variant="link"
                onClick={onSourceLinkClick}
                aria-label="Open source"
                className="[&_svg]:size-4 text-xs text-subtle gap-1"
              >
                {sourceIcon && <span className="shrink-0 flex items-center">{sourceIcon}</span>}
                <span>{sourceDescription}</span>
              </Button>
            ) : (
              <span className="[&_svg]:size-4 flex items-center gap-1 text-xs text-subtle">
                {sourceIcon && <span className="shrink-0 flex items-center">{sourceIcon}</span>}
                {sourceDescription}
              </span>
            ))}
        </div>
        {showActionButtons && (
          <ButtonGroup orientation="horizontal" rounded={false} aria-label="Suggestion actions" className="gap-1">
            {suggestionReasonBody && (
              <Popover open={whyOpen} onOpenChange={setWhyOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" aria-label={REASON_TITLE}>
                    <Lightbulb aria-hidden="true" />
                    Why
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="bottom"
                  className="z-above-modal shadow-md dark:shadow-md-invert border-dark-300"
                >
                  <div className="font-bold text-sm">{REASON_TITLE}</div>
                  <div className="text-sm text-subtle mt-1">{suggestionReasonBody}</div>
                </PopoverContent>
              </Popover>
            )}
            <Button variant="ghost" onClick={onRejectAllSuggestions}>
              <Backspace aria-hidden="true" />
              Reject
            </Button>
            {onAcceptAndPublish ? (
              <DropdownMenu>
                <div
                  ref={splitButtonRef}
                  className={cn('flex items-center rounded border border-border bg-primary', brandShadowEffect)}
                >
                  <Button
                    variant="ghost"
                    className="gap-1 rounded-none border-0 bg-transparent shadow-none hover:bg-dark-200"
                    onClick={onAcceptAllSuggestions}
                  >
                    <CheckCircle aria-hidden="true" />
                    Accept as draft
                  </Button>
                  <span className="h-full w-px self-stretch bg-foreground" aria-hidden="true" />
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-9 rounded-none border-0 bg-transparent shadow-none hover:bg-dark-200"
                      aria-label="More accept options"
                    >
                      <CaretDown className="size-4" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                </div>
                <DropdownMenuContent align="end" className="z-above-modal border-none shadow-md dark:shadow-md-invert">
                  <DropdownMenuItem onClick={onAcceptAndPublish}>
                    <CheckCircle aria-hidden="true" />
                    Accept & publish
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="shadow" onClick={onAcceptAllSuggestions}>
                <CheckCircle aria-hidden="true" />
                Accept as draft
              </Button>
            )}
          </ButtonGroup>
        )}
      </div>
    </div>
  );
});
